import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileText, ArrowLeft, PhoneCall } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

export default function Terms() {
  const location = useLocation();
  const isPublic = !location.pathname.startsWith('/app');

  const cardClass = isPublic ? "bg-slate-800/50 border-slate-700" : "";

  return (
    <div className={isPublic ? "min-h-screen bg-slate-950 text-slate-100" : ""}>
      {/* Public header */}
      {isPublic && (
        <div className="bg-slate-950 border-b border-slate-800">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
                <PhoneCall className="w-4 h-4 text-white" />
              </div>
              <span className="text-lg font-semibold text-white">CONNECT</span>
            </Link>
            <Link to="/">
              <Button variant="ghost" className="text-slate-300 hover:text-white hover:bg-slate-800">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      )}

      <div className="p-6 max-w-4xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${isPublic ? "bg-cyan-500/20" : "bg-primary/10"}`}>
              <FileText className={`w-6 h-6 ${isPublic ? "text-cyan-400" : "text-primary"}`} />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Terms & Conditions</h1>
              <p className={isPublic ? "text-slate-400" : "text-muted-foreground"}>Last updated: January 2025</p>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <Card className={cardClass}>
            <CardHeader>
              <CardTitle>1. Acceptance of Terms</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm dark:prose-invert max-w-none">
              <p>
                By accessing and using the CONNECT Cloud PBX Operator Console ("Service"), you accept 
                and agree to be bound by these Terms and Conditions. If you do not agree to these terms, 
                please do not use the Service.
              </p>
              <p>
                These terms apply to all users of the Service, including administrators, operators, 
                and any other personnel granted access by your organization.
              </p>
            </CardContent>
          </Card>

          <Card className={cardClass}>
            <CardHeader>
              <CardTitle>2. Description of Service</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm dark:prose-invert max-w-none">
              <p>CONNECT provides:</p>
              <ul>
                <li>WebRTC-based softphone functionality for making and receiving calls</li>
                <li>Contact management and directory services</li>
                <li>Call history and analytics</li>
                <li>Queue monitoring and management</li>
                <li>Multi-user support with role-based access</li>
              </ul>
              <p>
                The Service operates in conjunction with your Cloud PBX provider and requires valid 
                SIP credentials and an active account with a supported provider.
              </p>
            </CardContent>
          </Card>

          <Card className={cardClass}>
            <CardHeader>
              <CardTitle>3. User Accounts</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm dark:prose-invert max-w-none">
              <h4>Account Creation</h4>
              <p>
                You are responsible for maintaining the confidentiality of your account credentials. 
                You agree to notify your administrator immediately of any unauthorized use of your 
                account.
              </p>

              <h4>Account Responsibilities</h4>
              <p>You agree to:</p>
              <ul>
                <li>Provide accurate and complete information when creating your account</li>
                <li>Keep your login credentials secure and confidential</li>
                <li>Not share your account with others</li>
                <li>Accept responsibility for all activities under your account</li>
              </ul>
            </CardContent>
          </Card>

          <Card className={cardClass}>
            <CardHeader>
              <CardTitle>4. Acceptable Use</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm dark:prose-invert max-w-none">
              <p>You agree not to use the Service to:</p>
              <ul>
                <li>Make harassing, threatening, or fraudulent calls</li>
                <li>Violate any applicable laws or regulations</li>
                <li>Transmit malware or engage in any activity that could harm the Service</li>
                <li>Attempt to gain unauthorized access to other accounts or systems</li>
                <li>Resell or redistribute the Service without authorization</li>
                <li>Use automated systems to make calls without proper consent</li>
              </ul>
              <p>
                Violation of these terms may result in immediate suspension or termination of your 
                access to the Service.
              </p>
            </CardContent>
          </Card>

          <Card className={cardClass}>
            <CardHeader>
              <CardTitle>5. Service Availability</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm dark:prose-invert max-w-none">
              <p>
                We strive to maintain high availability of the Service, but do not guarantee 
                uninterrupted access. The Service may be temporarily unavailable due to:
              </p>
              <ul>
                <li>Scheduled maintenance</li>
                <li>System upgrades</li>
                <li>Emergency repairs</li>
                <li>Factors outside our control (network outages, force majeure)</li>
              </ul>
              <p>
                We will make reasonable efforts to notify users of planned maintenance in advance.
              </p>
            </CardContent>
          </Card>

          <Card className={cardClass}>
            <CardHeader>
              <CardTitle>6. Intellectual Property</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm dark:prose-invert max-w-none">
              <p>
                The Service, including its design, features, and content, is protected by copyright, 
                trademark, and other intellectual property laws. You may not copy, modify, distribute, 
                or create derivative works based on the Service without express written permission.
              </p>
              <p>
                You retain ownership of any content you create or upload to the Service, such as 
                contacts and notes. By using the Service, you grant us a license to process this 
                content as necessary to provide the Service.
              </p>
            </CardContent>
          </Card>

          <Card className={cardClass}>
            <CardHeader>
              <CardTitle>7. Limitation of Liability</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm dark:prose-invert max-w-none">
              <p>
                To the maximum extent permitted by law, we shall not be liable for any indirect, 
                incidental, special, consequential, or punitive damages arising from your use of 
                the Service, including but not limited to:
              </p>
              <ul>
                <li>Lost revenue or profits</li>
                <li>Loss of data</li>
                <li>Business interruption</li>
                <li>Missed calls or communication failures</li>
              </ul>
              <p>
                Our total liability for any claim arising from use of the Service shall not exceed 
                the amount you paid for the Service in the twelve months preceding the claim.
              </p>
            </CardContent>
          </Card>

          <Card className={cardClass}>
            <CardHeader>
              <CardTitle>8. Indemnification</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm dark:prose-invert max-w-none">
              <p>
                You agree to indemnify and hold harmless CONNECT, its officers, directors, employees, 
                and agents from any claims, damages, losses, or expenses (including legal fees) arising 
                from your use of the Service or violation of these Terms.
              </p>
            </CardContent>
          </Card>

          <Card className={cardClass}>
            <CardHeader>
              <CardTitle>9. Termination</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm dark:prose-invert max-w-none">
              <p>
                We may suspend or terminate your access to the Service at any time, with or without 
                cause, and with or without notice. Upon termination:
              </p>
              <ul>
                <li>Your right to use the Service will immediately cease</li>
                <li>You must discontinue all use of the Service</li>
                <li>We may delete your account and associated data</li>
              </ul>
              <p>
                Your organization's administrator may also revoke your access at any time.
              </p>
            </CardContent>
          </Card>

          <Card className={cardClass}>
            <CardHeader>
              <CardTitle>10. Changes to Terms</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm dark:prose-invert max-w-none">
              <p>
                We reserve the right to modify these Terms at any time. We will notify users of 
                material changes by posting a notice on the Service or sending an email. Your 
                continued use of the Service after changes become effective constitutes acceptance 
                of the new Terms.
              </p>
            </CardContent>
          </Card>

          <Card className={cardClass}>
            <CardHeader>
              <CardTitle>11. Governing Law</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm dark:prose-invert max-w-none">
              <p>
                These Terms shall be governed by and construed in accordance with the laws of 
                New Zealand, without regard to conflict of law principles. Any disputes arising 
                from these Terms shall be subject to the exclusive jurisdiction of the courts 
                of New Zealand.
              </p>
            </CardContent>
          </Card>

          <Card className={cardClass}>
            <CardHeader>
              <CardTitle>12. Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm dark:prose-invert max-w-none">
              <p>
                If you have any questions about these Terms & Conditions, please contact us 
                through the information provided on our{' '}
                <Link to={isPublic ? "/" : "/app/about"} className="text-primary hover:underline">
                  {isPublic ? "website" : "About page"}
                </Link>.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Footer for public page */}
        {isPublic && (
          <div className="mt-12 pt-6 border-t border-slate-800 text-center text-sm text-slate-500">
            <p>© {new Date().getFullYear()} Connect-US. All rights reserved.</p>
            <div className="flex justify-center gap-4 mt-2">
              <Link to="/privacy" className="hover:text-slate-300">Privacy Policy</Link>
              <span>•</span>
              <Link to="/" className="hover:text-slate-300">Home</Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
